import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import InternalServerErrorPage from '@/app/InternalServerErrorPage';
import MaintenancePage from '@/app/MaintenancePage';
import NotFoundErrorPage from '@/app/NotFoundErrorPage';
import ScheduleRoute from '@/features/schedule/components/ScheduleRoute';

import PrivateRoute from './privateRoute';

const Login = lazy(() => import('@/features/auth/views/Login'));
const Logout = lazy(() => import('@/features/auth/views/Logout'));

/** 고객사 */
const Client = {
  List: lazy(() => import('@/features/client/clientList/views/ClientList')),
  Form: lazy(() => import('@/features/client/clientForm/views/ClientForm')),
  Info: lazy(() => import('@/features/client/clientForm/views/ClientInfo')),
  Venue: {
    Main: lazy(() => import('@/features/venue/venueList/views/VenueMain')),
    List: lazy(() => import('@/features/venue/venueList/views/VenueListPage')),
    Form: {
      Venue: lazy(() => import('@/features/venue/venueForm/views/VenueForm')),
      // Space: lazy(() => import('@/features/space/spaceForm/views/SpaceForm')),
    },
    InfoMain: lazy(() =>
      import('@/features/venue/venueForm/views/VenueInfoMain')
    ),
    Info: lazy(() => import('@/features/venue/venueForm/views/VenueInfo')),
    SpaceInfo: lazy(() => import('@/features/space/spaceInfo/views/SpaceInfo')),
  },
  Manager: lazy(() =>
    import('@/features/client/managerEdit/views/ManagerEdit')
  ),
  ManagerCreate: lazy(() =>
    import('@/features/client/managerCreate/views/ManagerCreate')
  ),
  ManagerList: lazy(() =>
    import('@/features/client/managerList/views/ManagerList')
  ),
  CrewCreate: lazy(() =>
    import('@/features/client/crewCreate/views/CrewCreate')
  ),
  CleanierCreate: lazy(() =>
    import('@/features/client/cleanierCreate/views/CleanierCreate')
  ),
};

/** 업무관리 */
const Task = {
  Route: lazy(() => import('@/features/dashboard/components/TaskRoute')),
  ClientEmpty: lazy(() =>
    import('@/features/dashboard/components/ClientEmpty')
  ),
  Work: {
    Dashboard: lazy(() => import('@/features/dashboard/views/VenueDashboard')),
    Attendance: lazy(() =>
      import('@/features/attendance/views/AttendanceListPage')
    ),
    List: lazy(() => import('@/features/tickets/views/TicketList')),
    ScheduleRoute: lazy(() =>
      import('@/features/schedule/components/ScheduleRoute')
    ),
    ScheduleDashboard: lazy(() =>
      import('@/features/dashboard/views/ScheduleDashboard')
    ),
    Schedule: lazy(() => import('@/features/schedule/views/ScheduleSetting')),
  },
  Setting: {
    Operating: lazy(() => import('@/features/settings/views/Operating')),
    Crew: lazy(() => import('@/features/settings/views/Crew')),
  },
  Invoice: lazy(() => import('@/features/invoice/InvoiceContainer')),
};

const PartnersPage = lazy(() =>
  import('@/features/partners/views/PartnersPage')
);
const CrewListPage = lazy(() =>
  import('@/features/partners/views/CrewListPage')
);
const CrewPage = lazy(() => import('@/features/partners/views/CrewPage'));
const NewCrewPage = lazy(() => import('@/features/partners/views/NewCrewPage'));

/** 공지사항 */
const NoticesPage = lazy(() => import('@/features/notices/views/NoticesPage'));
const CreateNoticePage = lazy(() =>
  import('@/features/notices/views/CreateNoticePage')
);
const NoticeIdPage = lazy(() =>
  import('@/features/notices/views/NoticeIdPage')
);
const UpdateNoticeIdPage = lazy(() =>
  import('@/features/notices/views/UpdateNoticeIdPage')
);

/** 마이페이지 */
const AccountPage = lazy(() => import('@/features/account/AccountPage'));

/** 계약 관리 */
const Contract = {
  List: lazy(() => import('@/features/contract/list')),
  Client: lazy(() => import('@/features/contract/client')),
  Service: lazy(() => import('@/features/contract/service')),
  Info: lazy(() => import('@/features/contract/info')),
  Schedule: lazy(() => import('@/features/contract/schedule')),
};

/** 요청 게시판
const Board = {
  RequestList: lazy(() => import('@/features/requestBoard/views/RequestList')),
  RequestDetail: lazy(() =>
    import('@/features/requestBoard/views/RequestDetail')
  ),
};
*/

const Report = {
  NotUploaded: lazy(() => import('@/features/report/views/ReportDashBoard')),
  Attendance: lazy(() => import('@/features/report/views/AttendanceReport')),
  Monthly: lazy(() => import('@/features/report/views/ReportMonthly')),
  ManagerVenues: lazy(() =>
    import('@/features/report/views/ManagerVenueExport')
  ),
};

/** 문의/답변 */
const Chat = {
  Route: lazy(() => import('@/features/chat/ChatRoute')),
  Dashboard: lazy(() => import('@/features/chat/ChatDashboard')),
  App: lazy(() => import('@/features/chat/ChatApp')),
  ChatMobile: lazy(() => import('@/features/chat/ChatMobile')),
};

/** 알림 센터 (모바일 전용)*/
const Alarmcenter = lazy(() =>
  import('@/features/alarmcenter/MobileAlarmcenter')
);

/** 업무 현황 */
const WorkStatusPage = lazy(() =>
  import('@/features/workStatus/WorkStatusDashboard')
);

/**
 * 라우터 추가시, 반드시 ./menu.tsx 도 정보를 추가해 주세요!
 */

const routes = () => {
  // useGA4();

  return (
    <Routes>
      {/* <Route path="/testbed" element={<TestBed />} /> */}
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />

      <Route path="/" element={<PrivateRoute />}>
        {/** spation.com 으로 진입시 리다이렉트 */}
        <Route index element={<Navigate to="/login" />} />

        <Route path="/alarmcenter" element={<Alarmcenter />} />

        {/* 계약 관리 */}
        <Route path="/contract">
          <Route path="" element={<Navigate replace to="list" />} />
          <Route path="list" element={<Contract.List />} />
          <Route path="client" element={<Contract.Client />} />
          <Route path="service" element={<Contract.Service />} />
          <Route path="info" element={<Contract.Info />} />
          <Route
            path="schedule/client/:clientId/venue/:venueId"
            element={<Contract.Schedule />}
          />
        </Route>

        {/* 요청 게시판 */}
        {/* <Route path="/request">
          <Route path="" element={<Navigate replace to="list" />} />
          <Route path="list" element={<Board.RequestList />} />
          <Route path="item" element={<Board.RequestDetail />} />
        </Route> */}

        {/** 마이페이지 */}
        <Route path="/account" element={<AccountPage />} />

        <Route path="/task">
          <Route path="" index element={<Task.Route />} />
          <Route path="/task/client" element={<Task.ClientEmpty />} />
          <Route
            path="/task/client/:clientId"
            element={<Task.Work.Dashboard />}
          />

          {/* 업무관리 */}
          <Route path="/task/client/:clientId/venue/:venueId">
            <Route path="" element={<Task.Work.Dashboard />}>
              {/* 작업관리 */}
              <Route path="" element={<Task.Work.List />} />
              {/* 근태관리 */}
              <Route path="attendance" element={<Task.Work.Attendance />} />
            </Route>

            <Route path="schedule" element={<ScheduleRoute />}>
              <Route path="" element={<Task.Work.ScheduleDashboard />} />
            </Route>
            <Route path="setting">
              <Route path="operating" element={<Task.Setting.Operating />} />
              {/* <Route path="workname" element={<Task.Setting.Workname />} /> */}
              <Route path="crew" element={<Task.Setting.Crew />} />
            </Route>
            <Route path="invoice">
              <Route path="" element={<Task.Invoice />} />
            </Route>
          </Route>
        </Route>

        <Route path="/partners/crews/new" element={<NewCrewPage />} />
        <Route path="/partners/crews/:crewId" element={<CrewPage />} />

        <Route path="/partners" element={<PartnersPage />}>
          {/* Outlets(Subpages) here */}
          <Route path="" element={<Navigate replace to="crews" />} />
          <Route path="crews" element={<CrewListPage />} />
        </Route>

        <Route path="/client">
          <Route path="" element={<Client.List />} />
          <Route path="new" element={<Client.Form />} />
          <Route path=":clientId/edit" element={<Client.Form />} />
          <Route path="manager/bulk-edit" element={<Client.Manager />} />
          <Route path=":clientId" element={<Client.Venue.Main />}>
            <Route path="" element={<Client.Info />} />
            <Route path="venue" element={<Client.Venue.List />} />
          </Route>
          <Route
            path=":clientId/venue/new"
            element={<Client.Venue.Form.Venue />}
          />
          {/* <Route
						path=":clientId/venue/space/new"
						element={<Client.Venue.Form.Space />}
					/> */}
          <Route
            path=":clientId/venue/:venueId/edit"
            element={<Client.Venue.Form.Venue />}
          />
          {/* <Route
						path=":clientId/venue/:venueId/space/edit"
						element={<Client.Venue.Form.Space />}
					/> */}
          <Route
            path=":clientId/venue/:venueId"
            element={<Client.Venue.InfoMain />}
          >
            <Route path="" element={<Client.Venue.Info />} />
            <Route path="space" element={<Client.Venue.SpaceInfo />} />
          </Route>
        </Route>

        <Route path="notices">
          <Route index element={<NoticesPage />} />
          <Route path="new" element={<CreateNoticePage />} />
          <Route path=":noticeId" element={<NoticeIdPage />} />
          <Route path=":noticeId/edit" element={<UpdateNoticeIdPage />} />
        </Route>

        <Route path="/chat">
          <Route index element={<Chat.Route />} />
          <Route path="/chat/all" element={<Chat.Dashboard />}>
            <Route index element={<Chat.App />} />
          </Route>
          <Route path="/chat/client/:clientId" element={<Chat.Dashboard />}>
            <Route index element={<Chat.App />} />
            <Route
              path="/chat/client/:clientId/venue/:venueId"
              element={<Chat.App />}
            />
          </Route>
          <Route
            path="/chat/venue/:venueId/group/:groupId"
            element={<Chat.ChatMobile />}
          />
          {/* <Route path="/" element={<Chat.Dashboard />}>
          
          </Route> */}
        </Route>
        {/** 업무 현황 */}
        <Route path="/work-status" element={<WorkStatusPage />} />
        <Route path="/report">
          <Route path="not-uploaded" element={<Report.NotUploaded />} />
          <Route path="monthly" element={<Report.Monthly />} />
          <Route path="attendance" element={<Report.Attendance />} />
          <Route path="manager-venues" element={<Report.ManagerVenues />} />
        </Route>

        {/* 운영관리 (시스템 관리자 전용) */}
        <Route path="/admin">
          <Route path="manager" element={<Client.ManagerList />} />
          <Route path="manager/new" element={<Client.ManagerCreate />} />
          <Route path="crew/new" element={<Client.CrewCreate />} />
          <Route path="cleanier/new" element={<Client.CleanierCreate />} />
        </Route>
      </Route>

      <Route path="/error" element={<InternalServerErrorPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="*" element={<NotFoundErrorPage />} />
    </Routes>
  );
};

export default routes;
